import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './pStyles/DiscussionForum.css';

interface Review {
  id: number;
  volunteer_name: string;
  review_content: string;
  rating: number;
  created_at: string;
  is_anonymous?: boolean;
  is_approved?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

const DiscussionForum = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get<Review[]>(`${API_BASE_URL}/api/reviews`);
      // The API returns only approved reviews, so we can use them directly
      setReviews(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews. Please try again later.');
      setLoading(false);
    }
  };

  // Generate random avatar URL for display
  const getAvatarUrl = (index: number) => {
    const genders = index % 2 === 0 ? 'women' : 'men';
    return `https://randomuser.me/api/portraits/${genders}/${index % 100}.jpg`;
  };

  return (
    <div className="forum-page">
      <div className="forum-content">
        <div className="container">
          <div className="forum-hero">
            <h1 className="forum-title">Community Voices</h1>
            <p className="forum-subtitle">
              Real stories from community members and volunteers about their experiences and impact
            </p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p>Loading community reviews...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#e74c3c' }}>
              <p>{error}</p>
              <button 
                onClick={fetchReviews} 
                style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem 1.5rem', 
                  background: '#667eea', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: 'pointer' 
                }}
              >
                Try Again
              </button>
            </div>
          ) : reviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p>No reviews yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="stories-grid">
              {reviews.map((review, index) => {
                // Mark first review as featured
                const isFeatured = index === 0;
                // Use anonymous name if review is anonymous
                const displayName = review.is_anonymous ? 'Anonymous Volunteer' : review.volunteer_name;
                const displayRole = 'Volunteer';
                
                return (
                  <div key={review.id} className={`story-card ${isFeatured ? 'featured' : ''}`}>
                    {isFeatured && <span className="featured-badge">Featured</span>}
                    <div className="story-author">
                      <div className="author-image">
                        <img src={getAvatarUrl(index)} alt={displayName} />
                      </div>
                      <div className="author-info">
                        <h3 className="author-name">{displayName}</h3>
                        <p className="author-role">{displayRole}</p>
                        {review.rating && (
                          <div style={{ marginTop: '0.25rem', color: '#f59e0b' }}>
                            {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="story-content">
                      <div className="quote-icon">❝</div>
                      <p className="story-quote">{review.review_content}</p>
                    </div>
                    {review.created_at && (
                      <div style={{ 
                        marginTop: '1rem', 
                        fontSize: '0.875rem', 
                        color: '#6c757d',
                        textAlign: 'right'
                      }}>
                        {new Date(review.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscussionForum;
